import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CButton, CForm } from '@coreui/react';
import { Formik, FieldArray } from 'formik';
import * as Yup from 'yup';
import CIcon from '@coreui/icons-react';
import { toast } from 'react-toastify';
import { ConnectedAccountContext } from "../context/ConnectedAccountContext";
import Waiting from "../../../../../../general/Loadings/Waiting";
import StepCardHeader from "../components/StepCardHeaders";
import { useDispatch } from "react-redux";
import { setFlexibleModal } from "../../../../../../../actions/common";
import { API_CUSTOM_EXTERNAL_DATA_CONNECTION, COMPONENT_NAME } from "../../../../../../../constants";
import { EX_DATATYPE } from "../../../../../../../constants/external-data";
import { callExternalDataApi } from "../../../../../../../apiCaller";
import ObjectFormRow from "../components/ObjectFormRow";
import SyncFormikValues from "../../../../goals/intent-analysis/manage-intent-rules/components/SyncFormikValues";
import FormikDirtyWatcher from "../../../../goals/intent-analysis/manage-intent-rules/components/FormikDirtyWatcher";

const CURRENT_STEP = 3;

const RESERVED_OBJECT_NAMES = ['people', 'companies', 'users', 'sessions', 'events', 'customer', 'customerhistory', 'relations'];

const defaultObject = {
    name: '',
    unique_id: [],
    created_timestamp: null,
    modified_timestamp: null,
    is_deleted: null,
    other_object_fields: [],
};

const SELECT_ALL_OPTION = { value: '__SELECT_ALL__', label: 'Select All' };

const toOption = (options, val) => {
    if (!val) return null;
    if (typeof val === 'object') return val;
    return options.find(opt => opt.value === val) || null;
};

const toMultiOption = (options, arr) => {
    if (!Array.isArray(arr)) return [];
    return arr.map(val => toOption(options, val)).filter(Boolean);
};

const fetchExistedObject = async (id, accountId) => {
    try {
        const response = await callExternalDataApi(`${API_CUSTOM_EXTERNAL_DATA_CONNECTION}/${id}/existed-objects?accountId=${accountId}`, 'GET');

        const isSuccess = response?.data?.code === 200;
        const responseData = response?.data?.data;

        if (isSuccess) {
            return responseData;
        } else {
            throw new Error(response?.data?.message || 'Failed to fetch connection detail.');
        }
    } catch (err) {
        throw err;
    }
};

const Objects = () => {
    const {
        activeStep,
        setActiveStep,
        stepsData,
        setStepsData,
        initStepsData,
        stepSubmitSnapshot,
        setStepSubmitSnapshot,
        onFormDirty,
        canUpdate
    } = useContext(ConnectedAccountContext);

    const dispatch = useDispatch();
    const [existedObjects, setExistedObjects] = useState([]);

    const allFields = useMemo(() => (
        Array.isArray(stepsData.fields?.fields) ? stepsData.fields?.fields : []
    ), [stepsData.fields?.fields]);
    
    const allHeaders = useMemo(() => (
        Array.isArray(stepsData.fields?.headers) ? stepsData.fields?.headers : []
    ), [stepsData.fields?.headers]);

    const existedObjectNames = useMemo(() =>
        Array.isArray(stepsData.existedObjects)
            ? stepsData.existedObjects.map(o => o.object?.toLowerCase())
            : []
        , [stepsData.existedObjects]);

    useEffect(() => {
        if (activeStep === 3) {
            fetchExistedObject(stepsData.id, stepsData.accountId)
                .then((data) => {
                    setExistedObjects(data);
                });
        }
    }, [activeStep, stepsData.id, stepsData.accountId]);

    const validationSchema = Yup.object().shape({
        objects: Yup.array()
            .of(
                Yup.object().shape({
                    name: Yup.string()
                        .trim()
                        .required('Name is required')
                        .max(200, 'Name cannot be more than 200 characters')
                        .matches(
                            /^[a-zA-Z_][a-zA-Z0-9 _-]*$/,
                            'Name must start with a letter or underscore, and contain no special characters'
                        )
                        .test('unique', 'Object name must be unique', function (value) {
                            if (!value) return false;
                            const { options } = this;
                            const { objects } = options.context || {};
                            if (!objects) return true;
                            const count = objects.filter(o => o.name?.toLowerCase() === value.toLowerCase()).length;
                            return count <= 1;
                        })
                        .test('not-reserved', 'This object name is reserved. Please choose a different name.', function (value) {
                            if (!value) return false;
                            const v = value.trim().toLowerCase();
                            return !RESERVED_OBJECT_NAMES.includes(v);
                        })
                        .test('not-in-existed', function (value) {
                            if (!value) return false;
                            if (!existedObjectNames) return true;

                            const existed = existedObjects?.find(
                                o => o.object?.toLowerCase() === value.toLowerCase()
                            );

                            if (existed) {
                                return this.createError({
                                    message: `An object with this name already exists in connection "${existed.connectionName}". Please choose a different name.`
                                });
                            }

                            return true;
                        }),
                    unique_id: Yup.array()
                        .min(1, 'Please select at least one field')
                        .required('Unique ID is required'),
                    created_timestamp: Yup.object()
                        .nullable(),
                    modified_timestamp: Yup.object()
                        .nullable(),
                    is_deleted: Yup.object()
                        .nullable(),
                    other_object_fields: Yup.array()
                        .min(1, 'Please select at least one field')
                        .required('Other Object Fields is required'),
                })
            )
            .min(1, 'At least one object is required')
    }).concat(
        Yup.object().shape({})
    );

    const uniqueIdOptions = useMemo(
        () => {
            const filtered = allFields
                .filter(f => !f.ignore && (f.type === EX_DATATYPE.NUMERIC || f.type === EX_DATATYPE.STRING))
                .map(f => ({
                    value: f.value,
                    label: f.label || f.value,
                }));

            if (filtered.length === 0) {
                return [];
            }

            return filtered;
        },
        [allFields]
    );

    const timestampOptions = useMemo(
        () => {
            const filtered = allFields
                .filter(f => !f.ignore && f.type === EX_DATATYPE.TIMESTAMP)
                .map(f => ({
                    value: f.value,
                    label: f.label || f.value,
                }));

            if (filtered.length === 0) {
                return [];
            }

            return [{ value: '', label: 'Select a field' }, ...filtered];
        },
        [allFields]
    );

    const isDeletedOptions = useMemo(
        () => {
            const filtered = allFields
                .filter(f => !f.ignore && f.type === EX_DATATYPE.BOOLEAN)
                .map(f => ({
                    value: f.value,
                    label: f.label || f.value,
                }));

            if (filtered.length === 0) {
                return [];
            }

            return [{ value: '', label: 'Select a field' }, ...filtered];
        },
        [allFields]
    );

    const allFieldOptions = useMemo(
        () => allFields
            .map(f => ({
                value: f.value,
                label: f.label || f.value,
            })),
        [allFields]
    );

    const getOtherFieldOptions = (obj) => {
         const selectedOptions = obj.other_object_fields;
         const filtered = allFields.filter((f) => !f.ignore);
         const options = filtered.map((f) => ({
              value: f.value,
              label: f.label || f.value,
         }));

         const allSelected = selectedOptions?.length === filtered.length;

         if (allSelected) {
              return options;
         }

         return [SELECT_ALL_OPTION, ...options];
    };

    const defaultInitialValuesStep = useMemo(() => ({
        objects: (initStepsData.objects && initStepsData.objects.length > 0
            ? initStepsData.objects
            : [defaultObject]
        ).map(obj => ({
            id: obj.id,
            name: obj.name || '',
            unique_id: toMultiOption(uniqueIdOptions, obj.unique_id),
            created_timestamp: toOption(timestampOptions, obj.created_timestamp),
            modified_timestamp: toOption(timestampOptions, obj.modified_timestamp),
            is_deleted: toOption(isDeletedOptions, obj.is_deleted),
            other_object_fields: toMultiOption(allFieldOptions, obj.other_object_fields),
        })),
    }), [initStepsData, allFieldOptions, isDeletedOptions, timestampOptions, uniqueIdOptions]);

    const stepsDataStep = stepsData[CURRENT_STEP];
    const stepSnapshot = stepSubmitSnapshot[CURRENT_STEP];

    const initialValues = useMemo(() => {
        const base = stepsDataStep || stepSnapshot || defaultInitialValuesStep;

        const mergedObjects = base.objects.map(obj => {
            return {
                ...obj,
                unique_id: toMultiOption(
                    uniqueIdOptions,
                    obj.unique_id
                ),
                other_object_fields: toMultiOption(
                    allFieldOptions,
                    obj.other_object_fields
                ),
            };
        });

        return { ...base, objects: mergedObjects };
    }, [stepsDataStep, stepSnapshot, defaultInitialValuesStep, allFieldOptions, uniqueIdOptions]);

    const onSubmit = useCallback(async (values, { setSubmitting }) => {
        try {
            setSubmitting(true);

            const payload = {
                objects: values.objects.map((obj) => ({
                    id: obj.id || undefined,
                    name: obj.name.trim(),
                    unique_id: obj.unique_id.map((other) => other.value),
                    created_timestamp: obj.created_timestamp?.value,
                    modified_timestamp: obj.modified_timestamp?.value,
                    is_deleted: obj.is_deleted?.value,
                    other_object_fields: obj.other_object_fields.map((other) => other.value),
                })),
                lastEditStep: stepsData.lastEditStep && stepsData.lastEditStep > CURRENT_STEP
                    ? stepsData.lastEditStep
                    : CURRENT_STEP,
            }

            const response = await callExternalDataApi(
                `${API_CUSTOM_EXTERNAL_DATA_CONNECTION}/${stepsData.id}`,
                'PUT',
                payload
            );

            const isSuccess = response?.data?.code === 200;
            const responseData = response?.data?.data;

            if (isSuccess && responseData) {
                toast.success('Object Settings successfully.');

                dispatch(
                    setFlexibleModal({
                        show: false,
                        component: COMPONENT_NAME.CUSTOM_EXTERNAL_DATA_CONNECTED_ACCOUNTS,
                        ruleId: '',
                        attributes: {}
                    })
                );
                setStepSubmitSnapshot(prev => ({
                    ...prev,
                    [CURRENT_STEP]: { ...values },
                }));
            } else {
                const errMsg =
                    response?.data?.message ||
                    'API Error: Something went wrong. Please try again later.';

                toast.error(errMsg);
            }
        } catch (err) {
            console.error('Submit error:', err);
            toast.error('Something went wrong. Please try again later.');
        } finally {
            setSubmitting(false);
            onFormDirty(CURRENT_STEP, false);
        }
    }, [setActiveStep, setStepsData, stepsData]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <StepCardHeader
            title={`Objects`}
            step={CURRENT_STEP}
            activeStep={activeStep}
            lastEditStep={stepsData.lastEditStep}
            setActiveStep={() => setActiveStep(CURRENT_STEP)}
        >
            <p>Tell us what objects are contained in your data. Each object requires a field that will uniquely identify it's records, allowing you to upload modifications to a record in the future, and relate other data to the record. Each Connected Data set must include at least one object. Once an object is established here, you'll be able to manage it in the object settings.</p>

            <Formik
                key={stepsData.id}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
                validateOnBlur={true}
            >
                {({ values, errors, setFieldValue, isSubmitting, handleSubmit }) => {
                    return (
                        <CForm onSubmit={handleSubmit} noValidate>
                            <SyncFormikValues
                                values={values}
                                setStepsData={setStepsData}
                                step={CURRENT_STEP}
                            />
                            <FormikDirtyWatcher
                                step={CURRENT_STEP}
                                values={values}
                                snapshot={stepSubmitSnapshot[CURRENT_STEP]}
                                defaultInitialValues={defaultInitialValuesStep}
                                onDirty={onFormDirty}
                            />
                            <FieldArray name="objects">
                                {({ remove, push }) => (
                                    <>
                                        {values.objects.map((obj, idx) => {
                                            const existed = stepsData.existedAllFields?.find(e => e.id === obj.id);

                                            return (
                                                <ObjectFormRow
                                                    key={idx}
                                                    idx={idx}
                                                    obj={obj}
                                                    isImported={stepsData.importedByObjectIdObj?.[obj.id] ?? false}
                                                    isImportedUnique={stepsData.importedByObjectUniqueIdObj?.[obj.id] ?? false}
                                                    uniqueIdOptions={uniqueIdOptions}
                                                    timestampOptions={timestampOptions}
                                                    isDeletedOptions={isDeletedOptions}
                                                    selectAllOptions={SELECT_ALL_OPTION}
                                                    getOtherFieldOptions={getOtherFieldOptions}
                                                    remove={remove}
                                                    setFieldValue={setFieldValue}
                                                    showDelete={values.objects.length > 1}
                                                    existedValues={existed ? existed.values : []}
                                                    allHeaders={allHeaders}
                                                />
                                            )
                                        })}

                                        <CButton
                                            type="button"
                                            className="add-icon pl-0 d-flex d-flex align-items-center mb-4"
                                            onClick={() => push(defaultObject)}
                                        >
                                            <CIcon icon="iconAddField" className="mr-2" />
                                            <span className="text-add f-13">Add Object</span>
                                        </CButton>
                                    </>
                                )}
                            </FieldArray>

                            {typeof errors.objects === "string" && (
                                <div className="text-danger mb-2">{errors.objects}</div>
                            )}

                            <CButton disabled={isSubmitting || canUpdate} type="submit" className="px-4" color="primary">
                                <Waiting isLoading={isSubmitting}>
                                    {values.objects.some(obj => !obj.name?.trim())
                                        ? "Save Draft"
                                        : "Save & Next"}
                                </Waiting>
                            </CButton>
                        </CForm>
                    );
                }}
            </Formik>
        </StepCardHeader>
    );
};
export default Objects;